import React from 'react';
import { useI18n } from '../../context/I18nContext';
import Button from './Button';
import { Plus, Download, Upload } from 'lucide-react';
import { CONTROL_SM } from '../../styles/pageLayout';

/**
 * Configuration pour les actions d'une page
 */
export interface PageActionsConfig {
  /** Clé de traduction pour le bouton "New" */
  newButtonKey?: string;
  /** Callback pour créer un nouvel élément */
  onCreateNew?: () => void;
  /** Callback pour l'export */
  onExport?: () => void;
  /** Callback pour l'import */
  onImport?: () => void;
  /** Icône personnalisée pour le bouton "New" */
  newButtonIcon?: React.ReactNode;
  /** Variante du bouton "New" */
  newButtonVariant?: 'primary' | 'secondary' | 'outline';
  /**
   * Hide the New button below `sm` (use with TableFilters `mobilePrimaryAction`
   * so search + create share one row on phone).
   */
  hideNewOnMobile?: boolean;
  /** Stretch the New button full width below `sm` (no search bar beside it). */
  newButtonFullWidthOnMobile?: boolean;
  /** Actions personnalisées supplémentaires */
  customActions?: React.ReactNode;
}

/**
 * Props pour le composant PageActions
 */
interface PageActionsProps {
  /** Configuration des actions */
  config: PageActionsConfig;
  /** Classe CSS supplémentaire */
  className?: string;
}

const SOFT_PILL =
  `${CONTROL_SM} inline-flex items-center justify-center gap-1.5 rounded-full bg-gray-100/90 px-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200/80 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40`;

/**
 * Composant pour afficher les actions d'une page dans le header (New, Export, Import, etc.)
 */
const PageActions: React.FC<PageActionsProps> = ({
  config,
  className = ''
}) => {
  const { t } = useI18n();

  const {
    newButtonKey,
    onCreateNew,
    onExport,
    onImport,
    newButtonIcon = <Plus className="w-4 h-4" />,
    newButtonVariant = 'primary',
    hideNewOnMobile = false,
    newButtonFullWidthOnMobile = false,
    customActions
  } = config;

  const hasNew = Boolean(newButtonKey && onCreateNew);
  const hasImportExport = Boolean(onImport || onExport);
  if (!customActions && !hasNew && !hasImportExport) return null;

  // When New is mobile-hidden and there are no custom actions, the whole bar is desktop-only
  // (import/export already are). Avoid an empty gap under the title on phone.
  const desktopOnlyBar = hideNewOnMobile && !customActions;

  return (
    <div
      className={`flex flex-col sm:flex-row gap-2 ${
        newButtonFullWidthOnMobile ? 'w-full sm:w-auto' : ''
      } ${desktopOnlyBar ? 'hidden sm:flex' : ''} ${className}`}
    >
      {customActions && (
        <div className="flex w-full items-center gap-2 sm:w-auto">
          {customActions}
        </div>
      )}

      <div
        className={`flex flex-col sm:flex-row sm:items-center gap-2 ${
          newButtonFullWidthOnMobile ? 'w-full sm:w-auto' : ''
        }`}
      >
        {hasNew && (
          <div
            className={`flex-shrink-0 ${
              newButtonFullWidthOnMobile ? 'w-full sm:w-auto' : ''
            } ${hideNewOnMobile ? 'hidden sm:block' : ''}`}
          >
            <Button
              variant={newButtonVariant}
              size="sm"
              onClick={onCreateNew}
              icon={newButtonIcon}
              className={`!rounded-full shadow-sm shadow-primary-600/20 ${
                newButtonFullWidthOnMobile ? 'w-full sm:w-auto' : ''
              }`}
            >
              {t(newButtonKey!)}
            </Button>
          </div>
        )}

        {(onImport || onExport) && (
          <div className="hidden sm:flex items-center gap-1.5">
            {onImport && (
              <button type="button" onClick={onImport} className={SOFT_PILL}>
                <Upload className="h-4 w-4 shrink-0" />
                {t('common.import')}
              </button>
            )}
            {onExport && (
              <button type="button" onClick={onExport} className={SOFT_PILL}>
                <Download className="h-4 w-4 shrink-0" />
                {t('common.export')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageActions;
